import { get } from "lodash";

import React, { Component } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Container, Header, Segment } from "semantic-ui-react";
import { Formik, Form, Field } from "formik";

import * as Yup from "yup";
import * as Schemas from "../forms/validationSchemas.js";

import DebouncedFormikEffect from "../utils/DebouncedFormikEffect.jsx";
import FormikDetailsPanel from "../forms/formik/FormikDetailsPanel/FormikDetailsPanel.jsx";
import FormikSemanticDropdown from "../forms/formik/FormikSemanticDropdown.jsx";
import ImageDetailsView from "../images/imageDetailsPanel/ImageDetailsView.jsx";
import ImageEstimate from "./ImageEstimate.jsx";
import SegmentMessage from "../forms/messages/SegmentMessage.jsx";
import FormikActionsPanel from "../forms/formik/FormikActionsPanel.jsx";

import { IMAGES } from "../../state/actions/actionConstants.js";
import { MODES } from "../forms/formConstants.js";

import estimateSelector from "../../state/reducers/estimates/estimateSelector.js";
import { getImagesEstimate } from "../../state/reducers/projectWizard/projectWizardActions.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class CloneImageForm extends Component {
  static propTypes = {
    data: PropTypes.object,
    regionsList: PropTypes.array.isRequired,
    onCancel: PropTypes.func.isRequired,
    onReset: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    copyKey: PropTypes.string.isRequired,

    estimateIsAvailable: PropTypes.bool,
    estimates: PropTypes.array,
    estimatesIsFetching: PropTypes.bool.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    regionsList: [],
    copyKey: "image"
  };

  constructor(props) {
    super(props);

    this.schedules = ["default"];

    this.state = {
      initialValues: {
        name: "",
        description: "",
        region: undefined
      }
    };
  }

  // -- VALIDATION -- //

  validationScheme = () => {
    const messages = this.props.copy("forms.validation_requirements");

    return Yup.object().shape({
      name: Schemas.simpleStringSchema("Name", messages, true),
      description: Yup.string(),
      region: Schemas.dropdownSchema("Region", messages, true)
    });
  };

  // -- HANDLERS -- //

  doEstimate = (prevFormikProps, formikProps) => {
    const { data, updateEstimate } = this.props;

    if (
      !formikProps.values.region ||
      formikProps.values.ignoreChangeInSideEffect === true
    ) {
      return null;
    }

    const requestData = {
      "@id": "clone",
      name: formikProps.values.name,
      description: formikProps.values.description,
      region: formikProps.values.region,
      service: data.service,
      source: data["@id"]
    };

    updateEstimate(requestData);
  };

  onFormikSubmit = values => {
    const { data, onSubmit } = this.props;
    onSubmit({ ...values, service: data.service });
  };

  // -- RENDER -- //

  render() {
    const {
      data,
      regionsList,
      mode,
      onCancel,
      onReset,
      violationErrors,
      copyKey,
      estimateIsAvailable,
      estimates,
      estimatesIsFetching,
      copy
    } = this.props;

    const readOnly = mode === MODES.READ;
    const disabled = readOnly;

    return (
      <Formik
        initialValues={this.state.initialValues} // required by semantic-ui react
        validateOnMount={true}
        enableReinitialize={true}
        validationSchema={this.validationScheme()}
        onSubmit={this.onFormikSubmit}
      >
        {formikProps => {
          return (
            <Container className="main-responsive">
              <Header as="h3" className="clear-both">
                {copy(`${copyKey}.clone_image_title`)}
              </Header>

              <Form className="ui form clear-both">
                {estimateIsAvailable && (
                  <DebouncedFormikEffect onChange={this.doEstimate} />
                )}

                {get(formikProps.values, "requestAction.type", null) ===
                  IMAGES.POST && (
                  <SegmentMessage
                    title={copy(`${copyKey}.messages.created.title`)}
                    message={copy(`${copyKey}.messages.created.message`)}
                    dismissable={true}
                    positive={true}
                  />
                )}

                <Segment className="clear-both">
                  <Header as="h3" className="clear-both">
                    {copy(`${copyKey}.details_panel.details`)}
                  </Header>

                  <FormikDetailsPanel
                    readOnly={readOnly}
                    disabled={disabled}
                    apiErrors={violationErrors}
                  />
                </Segment>
                <Segment className="clear-both">
                  <Header as="h3" className="clear-both">
                    {copy(`${copyKey}.region_target.title`)}
                  </Header>
                  <p>{copy(`${copyKey}.region_target.body`)}</p>

                  <Field
                    name="region"
                    label={copy(`${copyKey}.region_target.region.label`)}
                    placeholder={copy(
                      `${copyKey}.region_target.region.placeholder`
                    )}
                    options={regionsList}
                    required={true}
                    apiErrors={violationErrors}
                    component={FormikSemanticDropdown}
                  />

                  {formikProps.values.region && estimateIsAvailable && (
                    <ImageEstimate
                      estimate={estimates[0]}
                      isFetching={estimatesIsFetching}
                      copyKey={copyKey}
                      copy={copy}
                    />
                  )}
                </Segment>

                {data && (
                  <Segment className="clear-both">
                    <Header as="h3" floated="left">
                      {copy(`${copyKey}.clone_source_title`)}
                    </Header>
                    <ImageDetailsView data={data} />
                  </Segment>
                )}

                <FormikActionsPanel
                  onCancel={onCancel}
                  reset={true}
                  onReset={onReset}
                  submit={true}
                  useSubmitModal={false}
                  modalCopy={copy(`${copyKey}.modals.create`)}
                  panelCopy={copy(`${copyKey}.actions_panel.create`)}
                />
              </Form>
            </Container>
          );
        }}
      </Formik>
    );
  }
}

const mapStateToProps = (state, props) => ({
  estimateIsAvailable: estimateSelector.selectEstimateIsAvailabile(
    state,
    props
  ),
  estimates: estimateSelector.selectEstimatesByAtIDs(["clone"])(state, props),
  estimatesIsFetching: state.estimates.isFetching,
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  updateEstimate: data => dispatch(getImagesEstimate(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(CloneImageForm);
